import { FIELD_ROW, liveScope, sameScope, scopeBlocks, type CoreScope } from './scope'
import type { Block, Blocks } from '@w/content-blocks'
import type { Doc, Edit, RemoteUpdate, TextCursor } from '@w/crdt'
import { pageDocument } from '../doc-bindings-for-page'
import { indexViewBlocks } from '../doc-blocks'
import type { CoreHistory } from './history'
import { canonicalPoint, children, firstPoint, gapOf, gapRowIndex, inCommittedRow, isAtom, locate, plain, pointAt, rows, type Row } from './tree-read'

/** Outside affinity distinguishes a native caret beside an annotation from one inside its last leaf. */
export interface CorePoint {
  affinity?: 'outside'
  blockId: string
  offset: number
}
export interface CoreSelection {
  nodes?: string[]
  cell?: { tableId: string; x: number; y: number }
  table?: { tableId: string; anchor: { x: number; y: number }; focus: { x: number; y: number } }
  /**
   * The caret sits at the start of this question's prompt, an inline field outside the row tree: a view that edits the
   * prompt places it there; `anchor` and `focus` are where a view without prompt editing places it.
   */
  field?: { blockId: string; role: 'prompt' }
  anchor: CorePoint
  focus: CorePoint
}

export interface CoreState {
  doc: Doc
  scope?: CoreScope
  /** Active editing container. The Doc remains the complete authored document. */
  blocks: Blocks
  selection: CoreSelection | null
  pendingFormat?: number
  /** Inline caret context for empty paragraphs, keyed by block identity; never persisted. */
  emptyParagraphFormats?: Record<string, number>
  history?: CoreHistory
}

/** A CoreState captures a persistent view; the Doc owns its evolving history. */
export function createCoreState(doc: Doc, selection: CoreSelection | null = null, scope?: CoreScope): CoreState {
  return { doc, scope, blocks: scopeBlocks(doc, scope), selection }
}

/** Command-local references exist only during the single transaction. Every mutation
 * primitive writes through Edit before refreshing the command's working references. */
export interface CommandState extends CoreState {
  tx: Edit
}

export function emptyParagraphFormat(state: CoreState, knownParagraph?: Block): number | undefined {
  if (!state.emptyParagraphFormats) return undefined
  const paragraph = emptyCaretParagraph(state, knownParagraph)
  return paragraph ? state.emptyParagraphFormats?.[formatKey(state, paragraph.id)] : undefined
}

/** A field's synthetic row is one id in every field; its format key names the owning field and slot instead. */
function formatKey(state: CoreState, id: string): string {
  const scope = state.scope
  return scope && id === FIELD_ROW ? `${FIELD_ROW}:${scope.blockId}.${scope.field}${scope.slot === undefined ? '' : `.${scope.slot}`}` : id
}

/**
 * A view's selection write. An unchanged selection returns `state` itself: no selection change happened, so the caret's
 * pending format survives and the typing group stays open. A changed one re-derives the pending format from the caret's
 * empty paragraph and, as a user-caused selection-only commit, closes the current typing group (Lexical history
 * classifies a selection-only update as OTHER, so the next keystroke never merges: lexical-history/src/index.ts:115-120,
 * 347-356). `closesGroup: false` is a selection step inside one platform-call reduction, which keeps the group open.
 */
export function writeSelection(state: CoreState, selection: CoreSelection,
  { closesGroup = true }: { closesGroup?: boolean } = {}): CoreState {
  if (state.selection && sameSelection(state.selection, selection)) return state
  const next: CoreState = { ...state, selection }
  next.pendingFormat = emptyParagraphFormat(next)
  const last = state.history?.undo.at(-1)
  if (closesGroup && state.history && last?.change !== undefined) {
    next.history = { undo: [...state.history.undo.slice(0, -1), Object.freeze({ ...last, change: undefined })], redo: state.history.redo }
  }
  return next
}

/** One value comparator per key of T (optional ones included), so a new field fails to compile until it has one. */
type Comparators<T> = { [K in keyof T]-?: (left: T[K], right: T[K]) => boolean }
type Cell = NonNullable<CoreSelection['cell']>
type TableRange = NonNullable<CoreSelection['table']>
type Coordinate = TableRange['anchor']

function sameBy<T extends object>(fields: Comparators<T>, left: T, right: T): boolean {
  return (Object.keys(fields) as (keyof T)[]).every((key) => fields[key](left[key], right[key]))
}
const equal = <V>(left: V, right: V) => left === right
const optional = <V>(same: (left: V, right: V) => boolean) => (left: V | undefined, right: V | undefined) =>
  left === undefined || right === undefined ? left === right : same(left, right)

const POINT: Comparators<CorePoint> = { blockId: equal, offset: equal, affinity: equal }
const COORDINATE: Comparators<Coordinate> = { x: equal, y: equal }
const CELL: Comparators<Cell> = { tableId: equal, x: equal, y: equal }
const TABLE_RANGE: Comparators<TableRange> = {
  tableId: equal,
  anchor: (left, right) => sameBy(COORDINATE, left, right),
  focus: (left, right) => sameBy(COORDINATE, left, right),
}
const SELECTION: Comparators<CoreSelection> = {
  anchor: (left, right) => sameBy(POINT, left, right),
  focus: (left, right) => sameBy(POINT, left, right),
  nodes: optional((left, right) => left.length === right.length && left.every((value, index) => value === right[index])),
  cell: optional((left, right) => sameBy(CELL, left, right)),
  table: optional((left, right) => sameBy(TABLE_RANGE, left, right)),
  field: optional((left, right) => left.blockId === right.blockId && left.role === right.role),
}

function sameSelection(left: CoreSelection, right: CoreSelection): boolean {
  return sameBy(SELECTION, left, right)
}

/** Retain only live empty paragraphs and remember their chosen typing format. */
export function rememberEmptyParagraphFormat(state: CoreState, knownParagraph?: Block): void {
  const formats = { ...state.emptyParagraphFormats }
  // An empty memory has nothing to reconcile; ordinary typing needs only its resolved row.
  const remembered = Object.keys(formats)
  if (remembered.length > 0) {
    const empty = new Set(rows(state.blocks)
      .filter((row) => row.block.type === 'p' && !plain(row.block))
      .map((row) => formatKey(state, row.block.id)))
    for (const id of remembered) if (!empty.has(id)) delete formats[id]
  }
  const paragraph = emptyCaretParagraph(state, knownParagraph)
  if (paragraph && state.pendingFormat !== undefined) formats[formatKey(state, paragraph.id)] = state.pendingFormat
  state.emptyParagraphFormats = Object.keys(formats).length > 0 ? formats : undefined
}

function emptyCaretParagraph(state: CoreState, knownParagraph?: Block): Block | undefined {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table ||
    selection.anchor.blockId !== selection.focus.blockId ||
    selection.anchor.offset !== selection.focus.offset) return undefined
  const paragraph = knownParagraph ?? rows(state.blocks).find((row) => row.block.id === selection.anchor.blockId)?.block
  return paragraph?.id === selection.anchor.blockId && paragraph.type === 'p' && !plain(paragraph) ? paragraph : undefined
}

/**
 * Imports a remote update for every view. Each endpoint whose anchor was removed is mapped like a
 * ProseMirror position through the deletion (prosemirror-transform map.ts: a position inside deleted
 * content maps to the deleted range's start), never clamped from its old offset; the live selection
 * and history-saved selections are mapped alike.
 */
export function importRemote(previous: CoreState, update: RemoteUpdate) {
  const imported = previous.doc.applyUpdate(update)
  // Block ids are stable across a crdt_rs import (no reset remap), so endpoints keep their ids.
  const id = (value: string) => value
  const scope = liveScope(previous.doc, previous.scope, id)
  const state: CoreState = { ...previous, ...createCoreState(previous.doc, null, scope) }
  // A field whose owner a peer removed leaves no container for its caret; the view falls back to no selection.
  if (previous.scope && !scope) {
    return { imported, state: { ...state, pendingFormat: undefined, history: previous.history } }
  }
  const map = (point: CorePoint) => mapPoint(previous.blocks, state.blocks, point, id)
  const live = previous.selection && [previous.selection.anchor, previous.selection.focus]
    .every((point) => anchored(state.blocks, { ...point, blockId: id(point.blockId) }))
  const selection = live || !previous.selection
    ? previous.selection
    : { anchor: map(previous.selection.anchor), focus: map(previous.selection.focus) }
  const history = previous.history && mapHistory(previous.history, map)
  return { imported, state: { ...state, ...(history ? { history } : {}),
    selection: assertAnchored(state.blocks, previous.selection, canonical(state.blocks, selection)) } }
}

/**
 * Text selections re-canonicalize after import: a filled empty row or host interior resolves to its
 * first leaf (prosemirror-state TextSelection.map: a non-inline head takes Selection.near). Node and
 * table selections keep their anchors.
 */
function canonical(blocks: Blocks, selection: CoreSelection | null): CoreSelection | null {
  if (!selection || selection.nodes || selection.table || selection.cell) return selection
  const anchor = canonicalPoint(blocks, selection.anchor)
  const focus = canonicalPoint(blocks, selection.focus)
  const same = (left: CorePoint, right: CorePoint) => left.blockId === right.blockId && left.offset === right.offset
  return same(anchor, selection.anchor) && same(focus, selection.focus) ? selection : { anchor, focus }
}

/**
 * A point anchored in `before` but gone from `after`, mapped per prosemirror-transform map.ts: inside a
 * surviving row it lands where the deleted content was (the end of the nearest surviving inline unit
 * before it, else the row start); a removed row or gap anchor takes `Selection.near` (prosemirror-state
 * selection.ts: forward first): the next surviving row's start, else the previous one's end, else the
 * document's first point. Points not anchored in `before` pass through for restoredSelection.
 */
function mapPoint(before: Blocks, after: Blocks, point: CorePoint, id: (value: string) => string): CorePoint {
  const current = point.blockId === 'root' ? point : { ...point, blockId: id(point.blockId) }
  if (anchored(after, current) || !anchored(before, point)) return current
  const old = rows(before)
  const fresh = rows(after)
  const survivor = (row: Row | undefined) => row && fresh.find((entry) => entry.block.id === id(row.block.id))
  const index = old.findIndex((row) => row.block.id === point.blockId || includes(row.block.children, point.blockId))
  const row = survivor(old[index])
  if (row) {
    const inline: Block[] = []
    const walk = (items: Blocks) => { for (const block of items) { inline.push(block); walk(children(block)) } }
    walk(old[index].block.children)
    const at = inline.findIndex((block) => block.id === point.blockId)
    for (const block of inline.slice(0, Math.max(at, 0)).toReversed()) {
      const unit = block.type === 'chars' || block.type === 'text' || block.type === 'br' || isAtom(block)
      const kept = unit && find(row.block.children, id(block.id))
      if (kept) return pointAt(row, locate([row], { blockId: kept.id,
        offset: kept.type === 'chars' || kept.type === 'text' ? plain(kept).length : 1 }).offset)
    }
    return pointAt(row, 0)
  }
  const gap = index < 0 ? gapOf(before, point) : undefined
  const split = index >= 0 ? index : gap ? gapRowIndex(before, gap) : 0
  const next = old.slice(index >= 0 ? index + 1 : split).map((entry) => survivor(entry)).find((entry) => entry !== undefined)
  if (next) return pointAt(next, 0)
  const previousRow = old.slice(0, split).map((entry) => survivor(entry)).findLast((entry) => entry !== undefined)
  return previousRow ? pointAt(previousRow, plain(previousRow.block).length) : firstPoint(after)
}

/** ProseMirror history maps its stored selections through remote steps; so does core history. */
function mapHistory(history: CoreHistory, map: (point: CorePoint) => CorePoint): CoreHistory {
  const selection = (saved: CoreSelection | null): CoreSelection | null => {
    if (!saved) return saved
    const anchor = map(saved.anchor)
    const focus = map(saved.focus)
    return anchor.blockId === saved.anchor.blockId && anchor.offset === saved.anchor.offset &&
      focus.blockId === saved.focus.blockId && focus.offset === saved.focus.offset ? saved : { anchor, focus }
  }
  const entry = (value: CoreHistory['undo'][number]) => {
    const selectionBefore = selection(value.selectionBefore)
    const selectionAfter = selection(value.selectionAfter)
    return selectionBefore === value.selectionBefore && selectionAfter === value.selectionAfter ? value
      : { ...value, selectionBefore, selectionAfter }
  }
  return { undo: history.undo.map((value) => entry(value)), redo: history.redo.map((value) => entry(value)) }
}

function find(items: Blocks, id: string): Block | undefined {
  for (const block of items) {
    if (block.id === id) return block
    const found = find(children(block), id)
    if (found) return found
  }
  return undefined
}

function includes(items: Blocks, id: string): boolean {
  return find(items, id) !== undefined
}

/** Undo/redo restores a saved selection unless its anchor is gone: then the caret sits where the documents first differ. */
export function restoredSelection(saved: CoreSelection | null, before: Blocks, after: Blocks): CoreSelection | null {
  return assertAnchored(after, saved, restore(saved, before, after))
}

function restore(saved: CoreSelection | null, before: Blocks, after: Blocks): CoreSelection | null {
  if (!saved || [saved.anchor, saved.focus].every((point) => anchored(after, point))) return saved
  const previous = rows(before)
  const current = rows(after)
  for (const [index, row] of current.entries()) {
    const old = previous[index]
    const text = plain(row.block)
    const oldText = old?.block.id === row.block.id ? plain(old.block) : ''
    if (old?.block.id === row.block.id && oldText === text) continue
    let common = 0
    while (common < text.length && text[common] === oldText[common]) common += 1
    const caret = pointAt(row, common)
    return { anchor: caret, focus: caret }
  }
  const last = current.at(-1)
  const caret = last ? pointAt(last, plain(last.block).length) : firstPoint(after)
  return { anchor: caret, focus: caret }
}

/** INV-1: an endpoint names an existing block, or is the document start `{root, 0}`. */
function anchored(blocks: Blocks, point: CorePoint): boolean {
  const present = (items: Blocks): boolean => items.some((block) => block.id === point.blockId || present(children(block)))
  return point.blockId === 'root' ? point.offset === 0 : inCommittedRow(blocks, point.blockId) || present(blocks)
}

/** INV-2: a removed anchor never survives an import or history step, and the selection is never dropped for it. */
function assertAnchored(blocks: Blocks, before: CoreSelection | null, after: CoreSelection | null): CoreSelection | null {
  if (before && !after) throw new Error('INV-2: a selection was dropped')
  for (const point of after ? [after.anchor, after.focus] : []) {
    if (!anchored(blocks, point)) throw new Error(`INV-2: selection anchor ${point.blockId}:${point.offset} does not exist`)
  }
  return after
}

/** A text endpoint anchored to its character in the Doc (Yjs relative position), so peer edits move it; null outside chars. */
export function textAnchor(doc: Doc, point: CorePoint, assoc: 'left' | 'right' = 'right'): TextCursor | null {
  const location = indexViewBlocks(pageDocument(doc)).get(point.blockId)
  return location?.block.type === 'chars' ? doc.textCursor(`${location.path}.text`, point.offset, assoc) : null
}

let anchorLocationVisits = 0

/** Block locations `anchoredPoint` examined since load: the per-resolve work behind cbe-remote-selection-anchor-resolve-indexed. */
export function anchorBlockVisits(): number {
  return anchorLocationVisits
}

/** Where `cursor` is now; null when its text is gone. */
export function anchoredPoint(doc: Doc, cursor: TextCursor): CorePoint | null {
  const at = doc.resolveTextCursor(cursor)
  if (!at?.text.endsWith('.text')) return null
  // Nested scalar lists (choice options) resolve with entity IDs, whereas indexBlocks uses [index]: the segment before
  // `.text` names the block, which the per-view index finds without scanning every block.
  const owner = at.text.slice(0, -'.text'.length)
  const location = indexViewBlocks(pageDocument(doc)).get(owner.slice(owner.lastIndexOf('.') + 1))
  if (location) anchorLocationVisits++
  return location ? { blockId: location.block.id, offset: at.offset } : null
}

/**
 * A selection with the field it was made in (none: the document), for dialogs and drags that act on it later. Text
 * endpoints carry Doc anchors that peer edits move; insertions at either edge stay outside. `doc` is the Doc it was
 * made in and `epoch` that Doc's epoch: a replaced Doc (even one with equal ids and epoch) or an identity remap (new
 * epoch) invalidates it whole, as it resets history.
 */
export interface SavedSelection { doc: Doc; scope?: CoreScope; selection: CoreSelection; epoch: number; anchors?: readonly [TextCursor | null, TextCursor | null] }

export function saveSelection(state: CoreState & { selection: CoreSelection }): SavedSelection {
  const { anchor, focus } = state.selection
  const entries = rows(state.blocks)
  const [from, to] = [locate(entries, anchor), locate(entries, focus)]
  // A range's start holds its next character and its end its previous one; a saved caret also holds its next character (restored later, not tracked live). The live remote import in core-editor anchors a caret 'left' instead.
  const order = Math.sign(from.row - to.row || from.offset - to.offset)
  const text = !state.selection.nodes && !state.selection.table
  return { doc: state.doc, scope: state.scope, selection: structuredClone(state.selection), epoch: state.doc.version().epoch,
    ...(text ? { anchors: [textAnchor(state.doc, anchor, order > 0 ? 'left' : 'right'), textAnchor(state.doc, focus, order < 0 ? 'left' : 'right')] } : {}) }
}

/** `saved` as it lives in `state.doc` now: its field entered again, or null when that field or an anchor is gone. */
export function liveSelection(state: CoreState, saved: SavedSelection): CoreState | null {
  if (saved.doc !== state.doc || saved.epoch !== state.doc.version().epoch) return null
  const scope = liveScope(state.doc, saved.scope)
  if (saved.scope && !scope) return null
  const blocks = scopeBlocks(state.doc, scope)
  const [anchor, focus] = [saved.selection.anchor, saved.selection.focus].map((point, index) => {
    const cursor = saved.anchors?.[index]
    return cursor ? anchoredPoint(state.doc, cursor) : point
  })
  if (!anchor || !focus || ![anchor, focus].every(point => anchored(blocks, point))) return null
  const selection = saved.anchors ? { ...saved.selection, anchor, focus } : saved.selection
  return { ...state, scope, blocks, selection, ...(sameScope(scope, state.scope) ? {} : { pendingFormat: undefined }) }
}

/** INV-1 at a commit: the selection lives in the state's active container (its scope's blocks), never in a stale one. */
export function assertLiveSelection(state: CoreState): void {
  assertAnchored(state.blocks, null, state.selection)
}

